import React from "react";

import { cn } from "@/app/handlers/cn.utils";

interface CompanySummary {
	company_name?: string;
	specialties?: string;
	company_industry?: string;
	headquarters?: string;
	company_website?: string;
	logo_url?: string;
	founded?: string | number;
	company_size_approx?: string;
	linkedin_url?: string;
}

interface CompanyProfileSummaryCardProps {
	company?: CompanySummary;
	isActivelyHiring?: boolean;
	showTabsShell?: boolean;
	action?: React.ReactNode;
	className?: string;
	cardPadding?: boolean;
	showJobsPreview?: boolean;
}

/**
 * Whether a field is worth rendering at all.
 *
 * These records are seeded from public sources, and a missing value arrives two ways: absent,
 * or the literal string "N/A". Both mean the same thing to a reader, so both count as empty —
 * about one company in ten stores "N/A" for its website and its LinkedIn URL, which would
 * otherwise render a chip saying nothing and a link pointing at https://N/A.
 */
const hasValue = (value?: string | number) => {
	if (typeof value === "number") return true;
	if (typeof value !== "string") return false;
	const trimmed = value.trim();
	return trimmed.length > 0 && trimmed.toUpperCase() !== "N/A";
};

/** A usable outbound link, or null when the record has nothing real to point at. */
const toExternalHref = (value?: string) => {
	if (!hasValue(value)) return null;
	const trimmed = (value as string).trim();
	return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

/** The card still needs a heading even for a record with no name, so this one keeps a fallback. */
const getValueOrNA = (value?: string) => (hasValue(value) ? (value as string).trim() : "N/A");

function Chip({ children, icon }: { children: React.ReactNode; icon: React.ReactNode }) {
	return (
		<span className="inline-flex max-w-full items-center gap-1 rounded-full bg-gray-100/80 px-2.5 py-0.5 font-medium text-gray-800">
			{/* shrink-0 so a long label wrapping inside a narrow column squashes the text, not the icon. */}
			<span className="flex shrink-0">{icon}</span>
			<span className="wrap-break-word">{children}</span>
		</span>
	);
}

const iconProps = {
	xmlns: "http://www.w3.org/2000/svg",
	fill: "none",
	viewBox: "0 0 24 24",
	strokeWidth: "1.5",
	stroke: "currentColor",
	"aria-hidden": true,
} as const;

const BuildingIcon = () => (
	<svg {...iconProps} className="h-3 w-3 text-gray-600">
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
		/>
	</svg>
);

const PinIcon = () => (
	<svg {...iconProps} className="h-3 w-3 text-gray-600">
		<path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
		/>
	</svg>
);

const CalendarIcon = () => (
	<svg {...iconProps} className="h-3 w-3 text-gray-600">
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
		/>
	</svg>
);

const UsersIcon = () => (
	<svg {...iconProps} className="h-3 w-3 text-gray-600">
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
		/>
	</svg>
);

const BriefcaseIcon = () => (
	<svg {...iconProps} className="h-3 w-3 text-green-600">
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 0 0 .75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 0 0-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0 1 12 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 0 1-.673-.38m0 0A2.18 2.18 0 0 1 3 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 0 1 3.413-.387m7.5 0V5.25A2.25 2.25 0 0 0 13.5 3h-3a2.25 2.25 0 0 0-2.25 2.25v.894m7.5 0a48.667 48.667 0 0 0-7.5 0M12 12.75h.008v.008H12v-.008Z"
		/>
	</svg>
);

const GlobeIcon = () => (
	<svg {...iconProps} className="w-5 h-5 text-gray-600">
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			d="m6.115 5.19.319 1.913A6 6 0 0 0 8.11 10.36L9.75 12l-.387.775c-.217.433-.132.956.21 1.298l1.348 1.348c.21.21.329.497.329.795v1.089c0 .426.24.815.622 1.006l.153.076c.433.217.956.132 1.298-.21l.723-.723a8.7 8.7 0 0 0 2.288-4.042 1.087 1.087 0 0 0-.358-1.099l-1.33-1.108c-.251-.21-.582-.299-.905-.245l-1.17.195a1.125 1.125 0 0 1-.98-.314l-.295-.295a1.125 1.125 0 0 1 0-1.591l.13-.132a1.125 1.125 0 0 1 1.3-.21l.603.302a.809.809 0 0 0 1.086-1.086L14.25 7.5l1.256-.837a4.5 4.5 0 0 0 1.528-1.732l.146-.292M6.115 5.19A9 9 0 1 0 17.18 4.64M6.115 5.19A8.965 8.965 0 0 1 12 3c1.929 0 3.716.607 5.18 1.64"
		/>
	</svg>
);

/**
 * LinkedIn's brand mark: one filled path whose counter-wound "in" knocks out to the card behind
 * it. The hairline outline this replaces read as a stray character rather than a logo at 20px.
 */
const LinkedInMark = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		fill="currentColor"
		aria-hidden="true"
		className="h-5 w-5"
	>
		<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
	</svg>
);

const CompanyProfileSummaryCard = ({
	company,
	isActivelyHiring = false,
	showTabsShell = false,
	action = null,
	className = "",
	cardPadding = true,
	showJobsPreview = false,
}: CompanyProfileSummaryCardProps) => {
	const companyName = getValueOrNA(company?.company_name);
	const industry = company?.company_industry;
	// The tagline under the name falls back to the industry so the line is never blank — 41% of
	// records carry no specialties of their own. Same order the companies list already uses.
	const specialties = hasValue(company?.specialties) ? company?.specialties : industry;
	const headquarters = company?.headquarters;
	const founded = company?.founded;
	const teamSize = company?.company_size_approx;
	const websiteHref = toExternalHref(company?.company_website);
	const linkedinHref = toExternalHref(company?.linkedin_url);

	return (
		<div
			className={cn(
				"w-full min-w-0 overflow-hidden rounded-xl bg-white",
				// A flush card lets content reach its edge, where rounded-xl plus overflow-hidden
				// clips it. On a narrow screen the wrapped name pushes the LinkedIn mark into that
				// corner and shaves it, so the corner gives way instead of the logo.
				!cardPadding && "max-sm:rounded-tr-none",
				className,
			)}
		>
			<div className={`${cardPadding ? "p-4 sm:p-6" : "p-0"}`}>
				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
						<div className="flex min-w-0 items-center gap-4">
							<div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-gray-100 bg-white sm:h-20 sm:w-20">
								<img
									alt={`${companyName} logo`}
									loading="lazy"
									className="h-full w-full object-cover"
									src={company?.logo_url || "/images/co-placeholder.png"}
									onError={(e) => {
										(e.target as HTMLImageElement).src = "/images/co-placeholder.png";
									}}
								/>
							</div>
							<div className="min-w-0">
								{/* Never wraps to its own line. The heading is the flexible item, so a short name
								    leaves the logo sitting right after it and a name long enough to wrap pushes
								    the logo to the far right. */}
								<div className="flex min-w-0 items-start gap-2">
									<h2 className="min-w-0 wrap-break-word text-xl font-bold text-gray-900 font-plus-jakarta sm:text-2xl">
										{companyName}
									</h2>
									{linkedinHref && (
										<a
											href={linkedinHref}
											target="_blank"
											rel="noreferrer"
											aria-label={`${companyName} on LinkedIn`}
											title={`${companyName} on LinkedIn`}
											// The box matches the heading's line height, so the mark centres on the
											// first line whether or not the name wraps. Aligning to the flex row
											// instead would ride high on one line and float mid-name on two.
											className="flex h-7 shrink-0 items-center text-[#0A66C2] transition-opacity hover:opacity-80 sm:h-8"
										>
											<LinkedInMark />
										</a>
									)}
								</div>
								{hasValue(specialties) && (
									<p className="mt-1 wrap-break-word text-sm text-gray-700 font-inter sm:text-base">{specialties}</p>
								)}
							</div>
						</div>

						{action && <div className="md:ml-4 md:shrink-0">{action}</div>}
					</div>

					{/* Founded and team size used to sit in a right-hand sidebar. They are facts, not
					    navigation, so they belong with the other facts — and the column they vacated
					    now goes to the job rows. */}
					<div className="flex flex-wrap gap-2 text-xs text-gray-700 font-inter">
						{hasValue(industry) && <Chip icon={<BuildingIcon />}>{industry}</Chip>}
						{hasValue(headquarters) && <Chip icon={<PinIcon />}>{headquarters}</Chip>}
						{hasValue(founded) && <Chip icon={<CalendarIcon />}>Founded {founded}</Chip>}
						{hasValue(teamSize) && <Chip icon={<UsersIcon />}>{teamSize} employees</Chip>}
						{isActivelyHiring && (
							<span className="inline-flex max-w-full items-center gap-1 rounded-full bg-success-light px-2.5 py-0.5 font-medium text-success-default">
								<BriefcaseIcon />
								Actively Hiring
							</span>
						)}
					</div>

					{/* Dropped entirely when there is no site, rather than leaving a "Website: N/A"
					    label that reads as a broken field. */}
					{websiteHref && (
						<div className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-gray-600 font-inter">
							<GlobeIcon />
							<span className="font-semibold">Website:</span>
							<a
								href={websiteHref}
								target="_blank"
								rel="noopener noreferrer"
								className="min-w-0 break-all font-medium text-primary-700 hover:underline"
							>
								{company?.company_website?.trim()}
							</a>
						</div>
					)}
				</div>
			</div>

			{showTabsShell && (
				<div className="border-t border-gray-200 px-4 sm:px-6">
					<nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Tabs">
						<span className="cursor-default whitespace-nowrap border-b-2 border-gray-900 py-3 px-1 text-sm font-semibold text-gray-900">
							Jobs
						</span>
						<span className="cursor-default whitespace-nowrap border-b-2 border-transparent py-3 px-1 text-sm font-semibold text-gray-500">
							About
						</span>
					</nav>
				</div>
			)}

			{showJobsPreview && (
				<div className="border-t border-gray-200 bg-gray-50/60 p-3 sm:p-4">
					<h3 className="text-base sm:text-lg font-semibold text-gray-900 font-plus-jakarta">Open Roles</h3>
					<div className="mt-3 space-y-2.5">
						{["Frontend Developer", "Backend Developer"].map((role) => (
							<div
								key={role}
								className={`items-center justify-between rounded-lg border border-gray-100 bg-white px-3 py-2 sm:px-4 sm:py-3 ${
									role === "Backend Developer" ? "hidden lg:flex" : "flex"
								}`}
							>
								<div className="min-w-0">
									<p className="truncate text-sm font-semibold text-gray-900">{role}</p>
									<p className="truncate text-xs text-gray-500">
										{hasValue(headquarters) ? `${headquarters} • ` : ""}Negotiable
									</p>
								</div>
								<button
									type="button"
									className="ml-3 rounded-md bg-primary-700 px-3 py-1.5 text-xs font-semibold text-white"
								>
									Explore
								</button>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

export default CompanyProfileSummaryCard;
